import jsPDF from "jspdf";
import "jspdf-autotable";

// Internal helper to create the PDF document object
const createPDFDoc = (data) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const primaryColor = [41, 128, 185]; 
  const grayColor = [100, 100, 100];

  // --- HEADER & LOGO ---
  if (data.business.logo) {
    const imgProps = doc.getImageProperties(data.business.logo);
    const pdfWidth = 40;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    doc.addImage(data.business.logo, 'JPEG', 15, 15, pdfWidth, pdfHeight);
  }

  // Business Details (Right Aligned)
  doc.setFontSize(10);
  doc.setTextColor(...grayColor);
  doc.text(data.business.name || "Business Name", pageWidth - 15, 20, { align: "right" });
  doc.text(data.business.address || "", pageWidth - 15, 25, { align: "right" });
  doc.text(data.business.phone || "", pageWidth - 15, 30, { align: "right" });
  doc.text(data.business.email || "", pageWidth - 15, 35, { align: "right" });

  // Title
  doc.setFontSize(18);
  doc.setTextColor(...primaryColor);
  doc.text("ESTIMATE", 15, 60);

  // --- LINE & DETAILS ---
  doc.setDrawColor(...grayColor);
  doc.setLineWidth(0.1);
  doc.line(15, 65, pageWidth - 15, 65);

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  
  // Client (Left)
  doc.setFont("helvetica", "bold");
  doc.text("Bill To:", 15, 75);
  doc.setFont("helvetica", "normal");
  doc.text(data.client.name || "", 15, 80);
  doc.text(data.client.address || "", 15, 85);
  doc.text(data.client.phone || "", 15, 90);

  // Meta (Right)
  const metaX = pageWidth - 60;
  doc.setFont("helvetica", "bold");
  doc.text("Estimate #:", metaX, 75);
  doc.text("Date:", metaX, 80);
  doc.setFont("helvetica", "normal");
  doc.text(data.meta.estimateNumber || "", pageWidth - 15, 75, { align: "right" });
  doc.text(data.meta.date || "", pageWidth - 15, 80, { align: "right" });

  // --- TABLE ---
  const tableRows = data.items.map(item => [
    item.description,
    item.quantity,
    `Rs. ${parseFloat(item.rate).toFixed(2)}`,
    `Rs. ${(item.quantity * item.rate).toFixed(2)}`
  ]);

  doc.autoTable({
    startY: 100,
    head: [['Item Description', 'Qty', 'Rate', 'Amount']],
    body: tableRows,
    theme: 'striped',
    headStyles: { 
      fillColor: primaryColor,
      halign: 'center' 
    },
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { cellWidth: 'auto', halign: 'left' }, 
      1: { cellWidth: 20, halign: 'center' },   
      2: { cellWidth: 35, halign: 'right' },    
      3: { cellWidth: 35, halign: 'right' }
    },
    didParseCell: function(data) {
      if (data.section === 'head') {
        if (data.column.index === 2 || data.column.index === 3) {
          data.cell.styles.halign = 'right';
        }
      }
    }
  });

  // --- TOTALS (FIXED FOR MOBILE OVERLAP) ---
  let finalY = doc.lastAutoTable.finalY + 10;
  
  // We align the Labels to the RIGHT (at -55) and Values to the RIGHT (at -15)
  // This creates a guaranteed 40-unit gap between the two columns.
  const labelEndX = pageWidth - 55; 
  const valueEndX = pageWidth - 15;

  doc.text("Subtotal:", labelEndX, finalY, { align: "right" });
  doc.text(`Rs. ${data.totals.subtotal.toFixed(2)}`, valueEndX, finalY, { align: "right" });
  
  finalY += 6;
  doc.text(`Tax (${data.totals.taxRate}%):`, labelEndX, finalY, { align: "right" });
  doc.text(`Rs. ${data.totals.taxAmount.toFixed(2)}`, valueEndX, finalY, { align: "right" });
  
  finalY += 6;
  doc.text(`Discount:`, labelEndX, finalY, { align: "right" });
  doc.text(`- Rs. ${data.totals.discount.toFixed(2)}`, valueEndX, finalY, { align: "right" });
  
  finalY += 6;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...primaryColor);
  doc.text("Total:", labelEndX, finalY, { align: "right" });
  doc.text(`Rs. ${data.totals.total.toFixed(2)}`, valueEndX, finalY, { align: "right" });

  if (data.notes) {
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "italic");
    doc.text("Notes: " + data.notes, 15, finalY + 15, { maxWidth: 100 });
  }

  return doc;
};

// --- EXPORT 1: DOWNLOAD FUNCTION ---
export const downloadPDF = (data) => {
  const doc = createPDFDoc(data);
  doc.save(`Estimate_${data.meta.estimateNumber}.pdf`);
};

// --- EXPORT 2: SHARE FUNCTION ---
export const sharePDF = async (data) => {
  const doc = createPDFDoc(data);
  const fileName = `Estimate_${data.meta.estimateNumber}.pdf`;
  
  const pdfBlob = doc.output('blob');
  const file = new File([pdfBlob], fileName, { type: "application/pdf" });

  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: 'Estimate PDF',
        text: `Here is the estimate for ${data.client.name}.`,
      });
    } catch (error) {
      console.log('Share canceled.');
    }
  } else {
    doc.save(fileName);
  }
};